// src/components/SocialMediaConnections.js
import React, { useState, useEffect } from 'react';
import { Instagram, Facebook, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { auth, db, isFirebaseInitialized } from '../config/firebase-config';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';

const PLATFORM_META = {
  instagram: {
    label: 'Instagram',
    iconBg: 'bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500',
    icon: <Instagram className="w-5 h-5 text-white" />,
  },
  facebook: {
    label: 'Facebook',
    iconBg: 'bg-blue-600',
    icon: <Facebook className="w-5 h-5 text-white" />,
  },
  tiktok: {
    label: 'TikTok',
    iconBg: 'bg-black',
    icon: (
      <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
        <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
      </svg>
    ),
  },
};

const SocialMediaConnections = () => {
  const [socialOAuth, setSocialOAuth] = useState({});

  useEffect(() => {
    if (!isFirebaseInitialized()) return;

    let unsubFirestore = null;
    let done = false;

    const unsubAuth = onAuthStateChanged(auth, (user) => {
      if (done) return;
      if (user) {
        const practRef = doc(db, 'practitioners', user.uid);
        unsubFirestore = onSnapshot(
          practRef,
          (snap) => setSocialOAuth(snap.data()?.socialConnections || {}),
          (err) => console.warn('SocialMediaConnections: Firestore error:', err)
        );
      } else {
        setSocialOAuth({});
      }
    });

    const timeout = setTimeout(() => { done = true; }, 3000);
    return () => {
      done = true;
      unsubAuth();
      clearTimeout(timeout);
      if (unsubFirestore) unsubFirestore();
    };
  }, []);

  const connectedPlatforms = ['facebook', 'instagram', 'tiktok'].filter(
    (p) => socialOAuth[p]?.connected
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm text-gray-600">
          Connection status for your verified social accounts.
        </p>
        <Link
          to="/dashboard/settings/integrations"
          className="text-sm font-medium hover:opacity-80 transition-opacity"
          style={{ color: '#bb9749' }}
        >
          Manage in Integrations →
        </Link>
      </div>

      {connectedPlatforms.length > 0 ? (
        connectedPlatforms.map((platformId) => {
          const meta = PLATFORM_META[platformId];
          const conn = socialOAuth[platformId];
          return (
            <div key={platformId} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 ${meta.iconBg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                    {meta.icon}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900">{meta.label}</p>
                    <p className="text-sm text-green-600 font-medium flex items-center gap-1">
                      <Check className="w-3 h-3" />
                      {conn.displayName || 'Connected'}
                    </p>
                  </div>
                </div>
                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded font-medium flex-shrink-0">
                  ✓ Verified
                </span>
              </div>
            </div>
          );
        })
      ) : (
        <p className="text-sm text-gray-500 italic">
          No social accounts connected yet.
        </p>
      )}
    </div>
  );
};

export default SocialMediaConnections;
